"use client";

import * as React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Link } from "@/components/common/Link";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Loader, Phone, Mail, CheckCircle2 } from "lucide-react";
function ContactFormConsumer() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 bg-primary-light border border-primary rounded-lg text-center max-w-2xl mx-auto">
        <CheckCircle2 className="size-16 text-primary mx-auto mb-4" />
        <h3 className="text-2xl font-bold text-foreground mb-2">{successMessage}</h3>
        <p className="text-muted-foreground mb-6">
          Wij nemen zo snel mogelijk contact met u op om uw keuring te plannen.
        </p>
        <Button onClick={resetForm} variant="outline" className="bg-transparent">
          Nog een aanvraag indienen
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6 max-w-2xl mx-auto">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "Naam is verplicht",
        minLength: {
          value: 2,
          message: "Minimaal 2 karakters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Naam *</FormLabel>
              <FormControl>
                <Input placeholder="Uw volledige naam" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "E-mail is verplicht",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Ongeldig e-mailadres"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>E-mail *</FormLabel>
              <FormControl>
                <Input type="email" placeholder="uw@email.be" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="phone" rules={{
      required: "Telefoonnummer is verplicht",
      pattern: {
        value: /^[\d\s+()-]+$/,
        message: "Ongeldig telefoonnummer"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Telefoonnummer *</FormLabel>
            <FormControl>
              <Input type="tel" placeholder="+32 123 45 67 89" {...field} />
            </FormControl>
            <FormDescription>
              Wij bellen u op dit nummer om de keuring te plannen
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Bericht is verplicht",
      minLength: {
        value: 10,
        message: "Minimaal 10 karakters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Uw vraag of opmerking *</FormLabel>
            <FormControl>
              <textarea placeholder="Vertel ons welke keuring u nodig heeft en voor welk type woning..." rows={5} className="flex w-full rounded-md border border-border-light bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 disabled:cursor-not-allowed disabled:opacity-50" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800 text-sm">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base bg-primary text-foreground-primary hover:bg-primary-hover hover:scale-[1.02] active:bg-primary-active focus-visible:ring-primary/40 transition-all duration-[120ms] ease-in-out">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Verzenden...
          </> : 'Verzend aanvraag'}
      </Button>
    </Form>;
}
export default function Contactformulier() {
  return <section className="bg-light-background py-24 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-foreground mb-4">Vul hieronder het formulier in</h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
            Wij nemen binnen 24 uur contact met u op om uw keuring te plannen
          </p>

          {}
          <div className="flex flex-col md:flex-row items-center justify-center gap-8 mb-12">
            <div className="flex items-center gap-3 text-left">
              <div className="bg-primary/10 p-3 rounded-lg">
                <Phone className="size-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Telefonisch contact</p>
                <Link to="tel:+32123456789" className="text-lg font-semibold text-primary hover:underline">0487412206</Link>
              </div>
            </div>
            <div className="flex items-center gap-3 text-left">
              <div className="bg-primary/10 p-3 rounded-lg">
                <Mail className="size-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">E-mail contact</p>
                <Link to="mailto:info@ekonorm.be" className="text-lg font-semibold text-primary hover:underline">
                  info@ekonorm.be
                </Link>
              </div>
            </div>
          </div>

          {}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-12">
            <div className="flex items-start gap-3 text-left">
              <CheckCircle2 className="size-5 text-primary mt-1 shrink-0" />
              <div>
                <p className="font-semibold text-foreground">Snelle reactie</p>
                <p className="text-sm text-muted-foreground">Binnen 24 uur antwoord</p>
              </div>
            </div>
            <div className="flex items-start gap-3 text-left">
              <CheckCircle2 className="size-5 text-primary mt-1 shrink-0" />
              <div>
                <p className="font-semibold text-foreground">Vrijblijvend advies</p>
                <p className="text-sm text-muted-foreground">Geen verplichtingen</p>
              </div>
            </div>
            <div className="flex items-start gap-3 text-left">
              <CheckCircle2 className="size-5 text-primary mt-1 shrink-0" />
              <div>
                <p className="font-semibold text-foreground">Transparante prijzen</p>
                <p className="text-sm text-muted-foreground">Inclusief alle stalen</p>
              </div>
            </div>
          </div>
        </div>

        <FormProvider className="bg-background border border-border-light rounded-lg shadow-medium p-8" formId="pricing-contact-form" sectionName="pricing-contact-section" defaultValues={{
        name: "",
        email: "",
        phone: "",
        message: ""
      }} validationRules={{
        name: {
          required: true,
          minLength: 2,
          maxLength: 100
        },
        email: {
          required: true,
          pattern: "email"
        },
        phone: {
          required: true,
          pattern: "phone"
        },
        message: {
          required: true,
          minLength: 10,
          maxLength: 1000
        }
      }} submitText="Verzend aanvraag" successMessage="Bedankt voor uw aanvraag!" errorMessage="Er is een fout opgetreden. Probeer het opnieuw of bel ons direct.">
          <ContactFormConsumer />
        </FormProvider>
      </div>
    </section>;
}